<template>
  <button
    type="button"
    class="back-link"
    :class="{ 'is-floating': floating }"
    :aria-label="label"
    @click="goBack"
  >
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
    <span class="back-link__label">{{ label }}</span>
  </button>
</template>

<script setup>
defineProps({
  label: { type: String, default: 'Back' },
  // Pages with a content column place this in the flow, above the heading.
  // A full-bleed page has no such column, so it gets pinned to the corner
  // instead.
  floating: { type: Boolean, default: false },
});

const router = useRouter();

// A button rather than a link to "/". A link starts a fresh navigation, which
// lands at the top of the home track — this is what made "back" lose the place
// you were reading. Going back through history instead restores the saved
// scroll position, so you return to the section you left from.
function goBack() {
  // Vue Router records the entry it came from. On a direct load or a pasted
  // link there is none, and back() would take somebody off the site entirely.
  if (typeof window !== 'undefined' && window.history.state?.back) {
    // Going back means the position that was left, never a hash. The entry
    // being returned to may still carry one from however it was first opened,
    // and acting on it would scroll to that section instead.
    try {
      sessionStorage.setItem('nav-back', '1');
    } catch {
      // private mode / storage disabled
    }
    router.back();
    return;
  }
  router.push('/');
}
</script>

<style lang="scss" scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: clamp(1.25rem, 3vh, 2rem);
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  svg {
    transition: transform 0.2s ease-in-out;
  }

  &:hover,
  &:focus-visible {
    color: var(--accent);

    svg {
      transform: translateX(-3px);
    }
  }
}

// Full-bleed pages only. Same control, pinned to the corner because there is no
// content column to sit above.
.back-link.is-floating {
  position: fixed;
  top: clamp(1.15rem, 3vh, 1.9rem);
  left: clamp(1rem, 3vw, 1.75rem);
  z-index: 2000;
  margin-bottom: 0;
  padding: 0.4rem 0.85rem 0.4rem 0.7rem;
  border-radius: 999px;
  // A flat fill rather than a backdrop-filter: this sits over content that
  // moves on every scroll frame, and re-blurring its backdrop each time
  // measured as a quarter of the frame budget elsewhere on the site.
  background: rgba(var(--background-rgb), 0.82);
  border: 1px solid rgba(var(--accent-rgb), 0.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  color: var(--text);

  &:hover,
  &:focus-visible {
    color: var(--accent);
    border-color: rgba(var(--accent-rgb), 0.5);
  }
}

@media (max-width: 768px) {
  .back-link.is-floating {
    padding: 0.4rem;

    .back-link__label {
      // The arrow carries it on a narrow screen; the word is what collides
      // with the theme toggle.
      display: none;
    }
  }
}
</style>
